import React, { useEffect, useRef, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";

import { faPlusCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ButtonVariant } from "Constant/button";
import Form from "react-bootstrap/Form";
import { useSelector } from "react-redux";

import StandartButton from "components/atoms/StandartButton";
import useHostFooterAction from "components/organisms/CreationModal/hooks/useHostFooterAction";

import {
    getAllSourceMapping,
    getSourceMappingFields,
    sendAllSourceUpdate,
} from "../helpers/Sources/createSource";
import MappingEmptyState from "./atoms/MappingEmptyState";
import SwalAlertComp from "./SwalAlert";

/**
 * Post-create field mapping editor. Pass `registerFooterAction` (+
 * `hideSaveButton`) to move Save into a host-owned footer; the host then also
 * owns scrolling, so the inner scroll area is disabled in that mode.
 */
const SourceMappingArea = ({
    sourceId,
    sourceType,
    isEditMapping,
    mappingSuccess,
    closeModal,
    closeButtonHidden,
    registerFooterAction,
    hideSaveButton = false,
}) => {
    const language = useSelector((state) => state.languagesState.languages);
    const [isSaveButtonLoading, setIsSaveButtonLoading] = useState(false);
    const [form, setForm] = useState({});
    const [mappings, setMappings] = useState([]);
    const [sourceFields, setSourceFields] = useState([]);
    const [allMergedFields, setAllMergedFields] = useState([]);
    const [requiredMergedFields, setRequiredMergedFields] = useState([]);
    const [noneSelectFields, setNoneSelectFields] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [loaderStatus, setLoaderStatus] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [checkErrorStatus, setCheckErrorStatus] = useState(false);
    const [mandotaryFields, setMandotaryFields] = useState([]);
    const [hasNoMapping, setHasNoMapping] = useState(false);
    const scrollAreaRef = useRef(null);
    const shouldScrollToBottom = useRef(false);

    useEffect(() => {
        componentRender();
    }, []);

    const componentRender = async () => {
        try {
            let response = await getSourceMappingFields(sourceId);
            if (response.status) {
                let source_fields = response?.data?.source_fields;
                let mapping_fields_all = response?.data?.mapping_fields?.all;
                let mapping_fields_required =
                    response?.data?.mapping_fields?.required;
                const defaultMappings =
                    response?.data?.mapping_fields?.default_mapping;
                if (
                    defaultMappings?.length &&
                    sourceType === "appsflyer" &&
                    !isEditMapping
                ) {
                    setMappings(
                        defaultMappings?.map(
                            ({ key, display_value, api_value }) => ({
                                property: key,
                                value: {
                                    value: api_value,
                                    label: source_fields?.find(
                                        (sourceField) =>
                                            sourceField?.api_value ===
                                            api_value,
                                    )?.display_value,
                                },
                            }),
                        ),
                    );
                }
                if (
                    source_fields.length > 0 &&
                    Object.keys(source_fields[0]).length > 1 &&
                    Object.keys(source_fields[0]).includes("api_value")
                ) {
                    source_fields = source_fields.map((x) => ({
                        value: x.api_value,
                        label: x.display_value,
                    }));
                } else {
                    source_fields = source_fields.map((x) => ({
                        value: x,
                        label: x,
                    }));
                }
                mapping_fields_all = Object.keys(mapping_fields_all).map(
                    (x) => ({
                        value: x,
                        label: mapping_fields_all[x],
                    }),
                );
                mapping_fields_required = Object.keys(
                    mapping_fields_required,
                ).map((x) => ({
                    value: x,
                    label: mapping_fields_required[x],
                }));
                let requiredValues = {};
                mapping_fields_required.forEach((item) => {
                    requiredValues[item.value] = "";
                });
                setSourceFields(source_fields);
                setAllMergedFields(mapping_fields_all);
                setNoneSelectFields(mapping_fields_all);
                if (isEditMapping) {
                    await isEditMappingCheck(
                        isEditMapping,
                        sourceId,
                        requiredValues,
                        mapping_fields_required,
                    );
                } else {
                    setRequiredMergedFields(mapping_fields_required);
                    setForm(requiredValues);
                }
            } else {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: response.message && response.message,
                    icon: "error",
                });
            }
        } finally {
            setInitialLoading(false);
        }
    };

    const setField = (field, value) => {
        setForm({
            ...form,
            [field]: value,
        });
    };

    const setMappingProperty = (index, value) => {
        const updatedItems = [...mappings];
        updatedItems[index] = { ...updatedItems[index], property: value };
        setMappings(updatedItems);
    };

    const setMappingValue = (index, value, label) => {
        const updatedItems = [...mappings];
        updatedItems[index] = {
            ...updatedItems[index],
            value: {
                value: value,
                label: label,
            },
        };
        setMappings(updatedItems);
    };

    const addNewRow = () => {
        let obj = { property: "", value: "" };
        shouldScrollToBottom.current = true;
        setMappings([...mappings, obj]);
    };

    useEffect(() => {
        if (!shouldScrollToBottom.current) {
            return;
        }
        shouldScrollToBottom.current = false;
        const dialog = scrollAreaRef.current?.closest('[role="dialog"]');
        let el = scrollAreaRef.current;
        while (el && el !== document.body && el !== document.documentElement) {
            if (dialog && !dialog.contains(el)) {
                break;
            }
            if (el.scrollHeight > el.clientHeight + 1) {
                el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
                return;
            }
            el = el.parentElement;
        }
    }, [mappings]);

    const deleteRow = (i, property) => {
        let updatedItems = [...mappings];
        updatedItems = updatedItems.filter((item, index) => index !== i);
        setMappings(updatedItems);
    };

    const saveMapping = async () => {
        setIsSaveButtonLoading(true);
        setCheckErrorStatus(true);
        let requiredRows = { ...form };
        let mappingRows = { ...form };
        mappings.forEach((item) => {
            mappingRows[item.property] = item?.value?.value;
        });
        if (
            [...Object.values(requiredRows)].filter((x) => x === "").length ===
                0 &&
            [...Object.values(mappingRows)].filter((x) => x === "").length === 0
        ) {
            let response = await sendAllSourceUpdate(
                { mappings: mappingRows },
                sourceId,
                sourceType !== undefined ? sourceType : "default",
            );
            if (response.status) {
                if (mappingSuccess) {
                    mappingSuccess(response);
                }
                if (closeModal) {
                    closeModal();
                }
            } else {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: response.message && response.message,
                    icon: "error",
                });
            }
        } else {
            SwalAlertComp({
                title: language?.mapping_modal?.warning ?? "Warning!",
                message:
                    language?.mapping_modal?.rows_empty ??
                    "There are empty mappings",
                icon: "error",
            });
        }
        setIsSaveButtonLoading(false);
    };

    const checkError = (val) => {
        if (checkErrorStatus) {
            return val === "" || val === null || val === undefined;
        } else {
            return false;
        }
    };

    useEffect(() => {
        let mappingRows = { ...form };
        mappings.forEach((item) => {
            mappingRows[item.property] = item.value;
        });
        let opts = [...allMergedFields];
        let using_opts = Object.keys(mappingRows);
        setNoneSelectFields(
            opts.filter((x) => using_opts.indexOf(x.value) === -1),
        );
    }, [mappings, form]);

    const mergeSelectFields = (val) => {
        const mergedField = [...allMergedFields].find((x) => x.value === val);

        return mergedField !== undefined
            ? [...noneSelectFields, mergedField]
            : [...noneSelectFields];
    };

    const getMergedSelectFields = (val) => {
        let _mergeSelectFields = [...mergeSelectFields(val)];
        let _mappings = [...mappings];
        _mappings = _mappings.filter((y) => y.property !== val);
        return _mergeSelectFields.filter(
            (x) => _mappings.filter((y) => y.property === x.value).length === 0,
        );
    };

    const isEditMappingCheck = async (
        editMappingStatus,
        sourceId,
        requiredValues,
        reqFields,
    ) => {
        if (editMappingStatus) {
            setLoaderStatus(true);
            let res = await getAllSourceMapping(sourceId);
            if (res.status) {
                let sourceMappings = res?.data;
                let reqList = [];
                let fieldList = [];
                if (
                    !sourceMappings?.mappings?.length &&
                    !sourceMappings?.has_missing_mandatory
                ) {
                    setHasNoMapping(true);
                }
                sourceMappings?.mappings?.forEach((item) => {
                    if (Object.keys(requiredValues).indexOf(item.key) > -1) {
                        let reqObj = reqFields.find(
                            (x) => x.value === item.key,
                        );
                        if (reqObj !== undefined) {
                            reqList.push(reqObj);
                            form[item.key] = item.api_value;
                        }
                    } else {
                        fieldList.push({
                            property: item.key,
                            value: {
                                value: item.api_value,
                                label: item.display_value,
                            },
                        });
                    }
                });
                if (sourceMappings?.has_missing_mandatory) {
                    setMandotaryFields(sourceMappings?.missing_mandatory_keys);
                    sourceMappings?.missing_mandatory_keys?.forEach((key) => {
                        fieldList.push({
                            property: {
                                label:
                                    reqFields?.find(
                                        (field) => field?.value === key,
                                    )?.label || key,
                                value: key,
                            },
                            value: {
                                value: "none",
                                label: "None",
                            },
                        });
                    });
                }
                setRequiredMergedFields(reqList);
                setMappings(fieldList);
                setLoaderStatus(false);
            } else {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: res.message && res.message,
                    icon: "error",
                });
                setLoaderStatus(false);
            }
        }
    };

    useEffect(() => {
        if (mappings && requiredMergedFields) {
            setRowCount(mappings.length + requiredMergedFields.length);
        }
    }, [mappings, requiredMergedFields]);

    const getPropertyDropdownValue = (property) => {
        let _mappings = [...mappings];
        let _fields = mergeSelectFields(property);
        let _mapping = _mappings.find((x) => x.property === property);
        if (_mapping !== undefined) {
            return {
                value: _mapping?.property || "",
                label: _fields.find((x) => x?.value === property)?.label || "",
            };
        }
    };

    useHostFooterAction(registerFooterAction, "mapping", {
        label: language?.buttons?.save ?? "Save",
        onSubmit: saveMapping,
        isLoading: isSaveButtonLoading,
        hidden: initialLoading || loaderStatus,
    });

    return (
        <div>
            <Box
                ref={scrollAreaRef}
                className={`modal-body position-relative z-1 tour-source-mapping-area`}
            >
                {(initialLoading || loaderStatus) && (
                    <div className="d-flex justify-content-center align-items-center py-4">
                        <CircularProgress color="primary" size={24} />
                    </div>
                )}
                {!initialLoading && !loaderStatus && hasNoMapping && (
                    <MappingEmptyState
                        title={
                            language?.source_modal?.no_mapping_title ??
                            "No mapping configured"
                        }
                        subtitle={
                            language?.source_modal?.no_mapping_subtitle ??
                            "This source has no field mappings yet. Use the form below to set one up."
                        }
                    />
                )}
                {!initialLoading &&
                    !loaderStatus &&
                    (isEditMapping === undefined ||
                        isEditMapping === false) && (
                        <div className="mb-4">
                            <h2
                                style={{
                                    fontSize: "24px",
                                    marginBottom: "10px",
                                }}
                            >
                                {language?.mapping_modal?.mappings ??
                                    "Mappings"}
                            </h2>
                            <div
                                dangerouslySetInnerHTML={{
                                    __html:
                                        language?.mapping_modal
                                            ?.mappings_header_content ??
                                        `
                            <p class="mb-3">Mapping between your source fields and SignalSight's system-defined fields is essential for the seamless processing of your signals within our platform. This ensures uninterrupted data flow and enhances the accuracy of conversions when transmitting data to your designated platform.</p>
                            <p>By aligning source fields with predefined system values, our platform optimizes data transmission efficiency and precision, facilitating seamless integration with your desired platform.</p>
                        `,
                                }}
                            />
                        </div>
                    )}
                {!initialLoading &&
                    !loaderStatus &&
                    !!requiredMergedFields.length &&
                    requiredMergedFields.map((item) => (
                        <div
                            className="row d-flex justify-content-start my-3"
                            data-cy="source-mapping-required-row"
                        >
                            <div className="col-5">
                                <Form.Group
                                    className="mb-3"
                                    controlId="exampleForm.ControlTextarea1"
                                >
                                    <FormControl
                                        style={{ width: "100%" }}
                                        error={checkError(form[item.value])}
                                    >
                                        <Autocomplete
                                            label={item.label}
                                            onChange={(event, selectedOpt) => {
                                                setField(
                                                    item.value,
                                                    selectedOpt?.value,
                                                );
                                                setCheckErrorStatus(true);
                                            }}
                                            value={{ label: form[item.value] }}
                                            required
                                            options={sourceFields}
                                            getOptionLabel={(option) =>
                                                option.label || ""
                                            }
                                            freeSolo
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    error={checkError(
                                                        form[item.value],
                                                    )}
                                                    label={item.label}
                                                />
                                            )}
                                        />
                                    </FormControl>
                                </Form.Group>
                            </div>
                            <div className="col-1 d-flex justify-content-center align-items-center">
                                <FontAwesomeIcon icon={faPlusCircle} />
                            </div>
                            <div className="col-5">
                                <FormControl style={{ width: "100%" }}>
                                    <TextField
                                        variant="outlined"
                                        placeholder={item.label}
                                        disabled
                                        style={{ backgroundColor: "#efefef" }}
                                    />
                                </FormControl>
                            </div>
                            <div className="col-1" />
                        </div>
                    ))}
                {!initialLoading &&
                    !loaderStatus &&
                    mappings.length > 0 &&
                    mappings.map((item, index) => (
                        <div className="row d-flex justify-content-between my-3">
                            <div className="col-5">
                                <Form.Group
                                    className="mb-3"
                                    controlId="exampleForm.ControlTextarea1"
                                >
                                    <FormControl style={{ width: "100%" }}>
                                        <Autocomplete
                                            label={
                                                language?.mapping_modal
                                                    ?.value_title ?? "Property"
                                            }
                                            value={item?.value}
                                            onChange={(event, selectedOpt) => {
                                                setMappingValue(
                                                    index,
                                                    selectedOpt?.value,
                                                    selectedOpt?.label,
                                                );
                                            }}
                                            options={sourceFields}
                                            getOptionLabel={(option) =>
                                                option?.label || ""
                                            }
                                            freeSolo
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={
                                                        language?.mapping_modal
                                                            ?.value ?? "Value"
                                                    }
                                                />
                                            )}
                                        ></Autocomplete>
                                    </FormControl>
                                </Form.Group>
                            </div>
                            <div className="col-1 d-flex justify-content-center align-items-center">
                                <FontAwesomeIcon icon={faPlusCircle} />
                            </div>
                            <div className="col-5">
                                <Form.Group
                                    className="mb-3"
                                    controlId="exampleForm.ControlTextarea1"
                                >
                                    <FormControl style={{ width: "100%" }}>
                                        {mandotaryFields?.includes(
                                            item?.property?.value,
                                        ) ? (
                                            <TextField
                                                variant="outlined"
                                                placeholder={
                                                    item.property.label
                                                }
                                                disabled
                                                style={{
                                                    backgroundColor: "#efefef",
                                                }}
                                            />
                                        ) : (
                                            <Autocomplete
                                                label={
                                                    language?.mapping_modal
                                                        ?.property_title ??
                                                    "Value"
                                                }
                                                value={getPropertyDropdownValue(
                                                    item.property,
                                                )}
                                                onChange={(
                                                    event,
                                                    selectedOpt,
                                                ) => {
                                                    setMappingProperty(
                                                        index,
                                                        selectedOpt?.value,
                                                    );
                                                }}
                                                options={getMergedSelectFields(
                                                    item.property,
                                                )}
                                                getOptionLabel={(option) =>
                                                    option.label
                                                }
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label={
                                                            language
                                                                ?.mapping_modal
                                                                ?.property ??
                                                            "Property"
                                                        }
                                                    />
                                                )}
                                            ></Autocomplete>
                                        )}
                                    </FormControl>
                                </Form.Group>
                            </div>
                            <div className="col-1 d-flex justify-content-center align-items-center mb-2">
                                <StandartButton
                                    iconButton
                                    variant={ButtonVariant.DANGER}
                                    onClick={() => {
                                        deleteRow(index, item.property);
                                    }}
                                >
                                    <DeleteOutlineIcon />
                                </StandartButton>
                            </div>
                        </div>
                    ))}
                {!initialLoading &&
                    !loaderStatus &&
                    noneSelectFields.length > 0 && (
                        <div className="row d-flex my-3 mx-1">
                            <StandartButton
                                variant={ButtonVariant.BLACK_OUTLINED}
                                onClick={addNewRow}
                            >
                                <AddIcon />
                                {rowCount === 0
                                    ? (language?.buttons?.add_a_row ??
                                      "Add a Row")
                                    : (language?.buttons?.add_new_row ??
                                      "Add New Row")}
                            </StandartButton>
                        </div>
                    )}
            </Box>
            {!hideSaveButton && !initialLoading && !loaderStatus && (
                <Box
                    className={`modal-footer${
                        isEditMapping &&
                        (closeButtonHidden === undefined ||
                            closeButtonHidden !== true)
                            ? " justify-content-between"
                            : ""
                    }`}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}
                >
                    {isEditMapping &&
                        closeModal &&
                        (closeButtonHidden === undefined ||
                            closeButtonHidden !== true) && (
                            <StandartButton
                                onClick={closeModal}
                                variant="contained"
                                color="error"
                                size="small"
                                className="max-w-140px"
                            >
                                {language?.buttons?.close ?? "Close"}
                            </StandartButton>
                        )}
                    <StandartButton
                        onClick={saveMapping}
                        variant="contained"
                        color="primary"
                        size="small"
                        className="mt-2"
                        isLoading={isSaveButtonLoading}
                        disabled={isSaveButtonLoading}
                        wrapperSx={{ ml: "auto" }}
                        data-cy="source-mapping-save"
                    >
                        {language?.buttons?.save ?? "Save"}
                    </StandartButton>
                </Box>
            )}
        </div>
    );
};

export default SourceMappingArea;
